import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState } from "react";
import { PlusOutlined } from "@ant-design/icons";
import { Button, Card, Empty, Table, Tabs, Typography } from "antd";
import { t } from "i18next";
import { debounce } from "lodash";
import { useAuthenticatedFetch } from "@/Api/Axios";
import * as Images from "@/Assets/Index";
import { getSessionStorageItem, isEmptyArray, objectToQueryParams, setSessionStorageItem } from "@/Utils/Index";
import CommonFilter from "./CommonFilter";
import CommonPagination from "./CommonPagination";

const { Text } = Typography;

const CommonTable = forwardRef((props, ref) => {
  const {
    isAdd = true,
    isFilterVisible = false,
    isPaginationVisible = true,
    tableData,
    promotedBulkActions,
    setParentFilters,
    parentFilters = [],
    setCount,
    setInitialData,
    localStorageKey,
    handleSubmit,
    showTab = false,
    tabOption = [],
    setParentSelectedTab,
    secondaryTabAction,
    handleParentSelectionChange = false,
    patentSelectedRows = [],
    parentAllRowsSelected = false,
    CustomiseComponent,
    setParentPagination,
  } = props;

  let localStorageItem;
  if (localStorageKey) localStorageItem = getSessionStorageItem(localStorageKey);
  const currentTabFilter = localStorageItem?.filter?.find?.((e) => e?.tabQuery);
  const [selectedItems, setSelectedItems] = useState([]);
  const [allDataSelectId, setAllDataSelectId] = useState([]);
  const [advanceFilter, setAdvanceFilter] = useState(localStorageItem?.filter || parentFilters);
  const [paginationAction, setPaginationAction] = useState();
  const [itemCount, setItemCount] = useState(0);
  const [selectedTab, setSelectedTab] = useState(currentTabFilter?.tabNumber || 0);
  const [selectedRows, setSelectedRows] = useState([]);
  const [allRowsSelected, setAllRowsSelected] = useState(false);
  const [hasInitialData, setHasInitialData] = useState(false);
  const fetch = useAuthenticatedFetch();
  const [pagination, setPagination] = useState(localStorageItem?.pagination || { page: 1, pageSize: 10 });
  const [data, setData] = useState({
    count: 0,
    rows: [],
  });

  const getInitialFilter = (pagination, advanceFilter) => {
    if (!pagination && !advanceFilter) {
      let localStorageItem;
      if (localStorageKey) localStorageItem = getSessionStorageItem(localStorageKey);
      pagination = localStorageItem?.pagination || { page: 1, pageSize: 10 };
      advanceFilter = localStorageItem?.filter || [];
    }
    setPagination(pagination);
    if (setParentPagination) {
      setParentPagination(pagination);
    }
    setAdvanceFilter(advanceFilter);
    return { pagination, advanceFilter };
  };

  useImperativeHandle(ref, () => ({
    fetchData: async (initialPagination, initialAdvanceFilter) => {
      const { pagination, advanceFilter } = getInitialFilter(initialPagination, initialAdvanceFilter);
      const queryParams = objectToQueryParams({ ...pagination });
      let response;
      if (isFilterVisible || props.fetchType === "post") {
        response = await fetch.post(`${props.url}${queryParams}`, { advanceFilter, ...props.queryParam });
      } else {
        response = await fetch.get(`${props.url}${queryParams}`);
      }
      if (response.code == 200) {
        setCount && setCount(response.data.count);
        setInitialData && setInitialData({ ...response.data });
        setData({ ...response.data });
      }
    },
  }));

  useEffect(() => {
    if (tableData) {
      setData(tableData);
    }
  }, [tableData]);

  // Custom selection state management for Ant Design Table
  const handleSelectionChange = useCallback(
    (newSelectedRowKeys, newSelectedRows) => {
      setSelectedRows(newSelectedRowKeys);
      setAllRowsSelected(newSelectedRowKeys.length === data.rows.length && data.rows.length > 0);
    },
    [data.rows]
  );

  const clearSelection = useCallback(() => {
    setSelectedRows([]);
    setAllRowsSelected(false);
  }, []);

  const selectedResources = selectedRows;
  const allResourcesSelected = allRowsSelected;

  useEffect(() => {
    if (handleParentSelectionChange) {
      setSelectedRows(patentSelectedRows);
      setAllRowsSelected(parentAllRowsSelected);
    } else {
      setSelectedRows(selectedResources);
      setAllRowsSelected(allResourcesSelected);
    }
  }, [patentSelectedRows, parentAllRowsSelected, selectedResources, allResourcesSelected]);

  useEffect(() => {
    props.clearSelection && clearSelection();
  }, [props.clearSelection]);

  useEffect(() => {
    if (allRowsSelected) {
      setAllDataSelectId(data.rows.map((item) => item.id));
    }
  }, [data.rows, allRowsSelected]);

  useEffect(() => {
    if (handleSubmit) handleSubmit(data.rows, selectedRows, allRowsSelected);
  }, [data.rows, selectedRows, allRowsSelected]);

  const rows = useMemo(() => {
    return props.rowsData(data.rows, allRowsSelected ? allDataSelectId : selectedRows);
  }, [data.rows, selectedRows, allDataSelectId, allRowsSelected]);

  useEffect(() => {
    ref.current.fetchData(pagination, advanceFilter);
    setParentFilters && setParentFilters(advanceFilter);
    localStorageKey && setSessionStorageItem(localStorageKey, { pagination, filter: advanceFilter });
  }, [advanceFilter, pagination]);

  const itemCountHandler = useCallback(
    debounce((data, selectedData, pagination, pageAction, itemCount) => {
      const parcialDataSelectId = data?.filter((item) => selectedData.includes(item.id)).map((item) => item.id);
      if (parcialDataSelectId?.length === data?.length) {
        setItemCount(selectedData?.length);
      } else {
        if (pageAction === "next" || parcialDataSelectId?.length === selectedData?.length) {
          setItemCount(data?.length * pagination.page);
        }
        // if (pageAction === "previous" || parcialDataSelectId.length != selectedData.length) {
        //   setItemCount(itemCount + data.length);
        // }
      }
    }, 500),
    [setItemCount]
  );

  useEffect(() => {
    itemCountHandler(data?.rows || [], selectedRows, pagination, paginationAction, itemCount); // wait for api call
  }, [data.rows, selectedRows, pagination, paginationAction]);

  // Table configuration for Ant Design
  const tableProps = useMemo(() => {
    const rowSelection = props.selectable
      ? {
          selectedRowKeys: selectedRows,
          onChange: handleSelectionChange,
          onSelectAll: (selected, selectedRows, changeRows) => {
            if (selected) {
              const allIds = data.rows.map((row) => row.id);
              setSelectedRows(allIds);
              setAllRowsSelected(true);
            } else {
              setSelectedRows([]);
              setAllRowsSelected(false);
            }
          },
        }
      : undefined;

    return {
      dataSource: data.rows,
      columns: props.columns || [],
      rowKey: "id",
      rowSelection,
      pagination: false, // We handle pagination separately
      scroll: { x: true },
      size: "middle",
    };
  }, [data.rows, selectedRows, props.columns, props.selectable, handleSelectionChange]);

  const handleTabChange = useCallback(
    async (tab) => {
      clearSelection();
      setAllRowsSelected(false);
      setSelectedRows([]);
      setData({ count: 0, rows: [] });
      setSelectedTab(tab);
      setParentSelectedTab && setParentSelectedTab(tab);
      const currentTabQuery = tabOption?.[tab]?.searchQuery;
      currentTabQuery ? setAdvanceFilter([{ ...currentTabQuery, tabNumber: tab }]) : setAdvanceFilter([]);
      setPagination({ page: 1, pageSize: 10 });
    },
    [advanceFilter]
  );

  useEffect(() => {
    setHasInitialData(!(advanceFilter?.length === 0 && (data.count === 0 || !data?.count)));
  }, [advanceFilter, data]);
  return (
    <Card style={{ padding: 0 }}>
      {showTab && (
        <Tabs
          activeKey={String(selectedTab)}
          onChange={(key) => handleTabChange(Number(key))}
          items={tabOption.map((tab, index) => ({
            key: String(index),
            label: tab.content || tab.label,
          }))}
        />
      )}
      {secondaryTabAction}
      {isFilterVisible && hasInitialData && (
        <CommonFilter
          {...props}
          filters={advanceFilter}
          setPagination={setPagination}
          setFilters={setAdvanceFilter}
          currentTabData={tabOption[selectedTab]}
        />
      )}
      {!hasInitialData && CustomiseComponent ? (
        CustomiseComponent
      ) : isEmptyArray(data.rows || rows) ? (
        <Empty
          description={`${t("common.CommonTable.No")} ${props.title} ${t("common.CommonTable.Found")}`}
          image={Images.EmptyReuslt || Empty.PRESENTED_IMAGE_SIMPLE}
        >
          {isAdd && (
            <Button type="primary" icon={<PlusOutlined />} onClick={props.handleAddClick}>
              {t("common.CommonTable.Add")} {props.title}
            </Button>
          )}
        </Empty>
      ) : (
        <>
          <Table {...tableProps} />
          {isPaginationVisible && (
            <CommonPagination
              {...props}
              filter={pagination}
              setFilter={setPagination}
              data={data}
              setPaginationAction={setPaginationAction}
            />
          )}
        </>
      )}
    </Card>
  );
});

export default CommonTable;
